import React from "react";
import { Button } from "@/components/ui/button";
import { EditableText, EditableTextarea } from "@/components/fields";
import { TabHeader, OrnateFrame } from "@/components/royal";
import { Plus, Trash2 } from "lucide-react";

const STATES = ["up", "neutral", "down"];
const COLORS = { up: "#22c55e", neutral: "#f59e0b", down: "#ef4444" };
const INDICATORS = ["safety", "economy", "morale"];

function Indicator({ label, state, editable, onCycle, testId }) {
  const s = state || "neutral";
  return (
    <button
      type="button"
      disabled={!editable}
      onClick={onCycle}
      data-testid={testId}
      className={`flex flex-col items-center gap-1.5 ${editable ? "cursor-pointer" : "cursor-default"}`}
    >
      <span className="w-5 h-5 rounded-full border border-black/20 shadow-inner"
        style={{ background: COLORS[s], boxShadow: `0 0 10px ${COLORS[s]}66` }} data-state={s} />
      <span className="ht-uppercase-label">{label}</span>
    </button>
  );
}

export default function Settlements({ player, updatePlayer, editable }) {
  const settlements = player.settlements || [];

  const add = () => updatePlayer((p) => {
    p.settlements = p.settlements || [];
    p.settlements.push({ id: crypto.randomUUID(), name: "New Settlement", description: "", safety: "neutral", economy: "neutral", morale: "neutral" });
  });
  const change = (id, k, v) => updatePlayer((p) => { const s = p.settlements.find((x) => x.id === id); if (s) s[k] = v; });
  const cycle = (id, k) => updatePlayer((p) => {
    const s = p.settlements.find((x) => x.id === id);
    if (s) { const i = STATES.indexOf(s[k] || "neutral"); s[k] = STATES[(i + 1) % STATES.length]; }
  });
  const remove = (id) => updatePlayer((p) => { p.settlements = p.settlements.filter((x) => x.id !== id); });

  return (
    <div className="space-y-6">
      <TabHeader label="Holdings" title="Settlements">
        {editable && (
          <Button variant="outline" size="sm" className="rounded-sm" onClick={add} data-testid="add-settlement">
            <Plus strokeWidth={1.5} className="w-4 h-4 mr-1" /> Settlement
          </Button>
        )}
      </TabHeader>

      {settlements.length === 0 ? (
        <div className="ht-panel p-12 text-center text-muted-foreground" data-testid="settlements-empty">No settlements.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="settlements-grid">
          {settlements.map((s, i) => (
            <OrnateFrame key={s.id} hover className="p-6 space-y-4" testId={`settlement-card-${i}`}>
              {editable ? (
                <EditableText value={s.name} editable onChange={(v) => change(s.id, "name", v)} placeholder="Settlement name" testId={`settlement-name-${i}`} />
              ) : (
                <h3 className="ht-title text-xl">{s.name}</h3>
              )}
              <EditableTextarea value={s.description} editable={editable} rows={3} onChange={(v) => change(s.id, "description", v)} placeholder="Description" testId={`settlement-desc-${i}`} />
              <div className="flex items-center gap-8 pt-3 border-t border-gold/20">
                {INDICATORS.map((ind) => (
                  <Indicator key={ind} label={ind} state={s[ind]} editable={editable}
                    onCycle={() => cycle(s.id, ind)} testId={`settlement-${i}-${ind}`} />
                ))}
              </div>
              {editable && (
                <Button variant="ghost" size="sm" className="rounded-sm text-destructive" onClick={() => remove(s.id)} data-testid={`settlement-remove-${i}`}>
                  <Trash2 strokeWidth={1.5} className="w-4 h-4 mr-1" /> Remove
                </Button>
              )}
            </OrnateFrame>
          ))}
        </div>
      )}
    </div>
  );
}
